import { i18n } from "../../translate/i18n";

import React, { useState, useEffect, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";

import { Button, TextField, Typography } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { IconButton, InputAdornment, Switch } from "@mui/material";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import EmailIcon from "@material-ui/icons/Email";
import LockIcon from "@material-ui/icons/Lock";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import { Helmet } from "react-helmet";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import defaultLoginLogo from "../../assets/login-logo-default.png";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

// resolve URL vinda do backend (relativa ou absoluta) com fallback
const resolveImageUrl = (value, fallback) => {
  if (!value) return fallback;
  if (value.startsWith("http")) return value;
  if (!backendUrl) return value;
  const base = backendUrl.replace(/\/+$/, "");
  const clean = value.replace(/^\/+/, "");
  return `${base}/${clean}`;
};

// Função para calcular força da senha
const calculatePasswordStrength = (password) => {
  let strength = 0;
  if (password.length >= 8) strength += 1;
  if (/[A-Z]/.test(password)) strength += 1;
  if (/[a-z]/.test(password)) strength += 1;
  if (/[0-9]/.test(password)) strength += 1;
  if (/[^A-Za-z0-9]/.test(password)) strength += 1;
  return strength;
};

const Login = () => {
  const theme = useTheme();
  const { handleLogin } = useContext(AuthContext);

  const [user, setUser] = useState({
    email: "",
    password: "",
    remember: false,
  });

  const [branding, setBranding] = useState({
    loginLogo: "/logo.png",
    loginBackground: null, // null = usa o padrão moderno
    loginWhatsapp: "https://wa.me/5500000000000",
  });

  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [userCreationEnabled, setUserCreationEnabled] = useState(true);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [focusedInput, setFocusedInput] = useState(null);
  const [isCardHover, setIsCardHover] = useState(false);
  const [isLoginHover, setIsLoginHover] = useState(false);
  const [isSignupHover, setIsSignupHover] = useState(false);
  const [showWhatsAppMenu, setShowWhatsAppMenu] = useState(false);
  const [isWhatsAppHover, setIsWhatsAppHover] = useState(false);
  const [showBalloon, setShowBalloon] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // ========= Tema e aparência da página =============
  useEffect(() => {
    try {
      localStorage.setItem("theme", "light");
    } catch (e) {}
    document.documentElement.classList.remove("dark");
    document.documentElement.setAttribute("data-theme", "light");
    document.body.classList.add("login-page");
    return () => document.body.classList.remove("login-page");
  }, []);

  // ========= Buscar settings globais ============
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const { data } = await api.get("/global-config/public-branding");

        setBranding({
          loginLogo: data.loginLogo || "/logo.png",
          loginBackground: data.loginBackground || null,
          loginWhatsapp: data.loginWhatsapp || "https://wa.me/5500000000000",
        });
      } catch (err) {
        console.error("Erro ao carregar branding:", err);
      }
    };

    fetchBranding();
  }, []);

  // ========== Verificar se cadastro está habilitado ==========
  useEffect(() => {
    const fetchUserCreationStatus = async () => {
      try {
        const { data } = await api.get("/settings/userCreation");
        setUserCreationEnabled(data.userCreation === "enabled");
      } catch (err) {
        setUserCreationEnabled(false);
      }
    };

    fetchUserCreationStatus();
  }, []);

  // Effect para resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effect para balão do WhatsApp
  useEffect(() => {
    const showBalloonInterval = setInterval(() => {
      if (!showWhatsAppMenu) {
        setShowBalloon(true);
        setTimeout(() => setShowBalloon(false), 5000);
      }
    }, 15000);

    return () => clearInterval(showBalloonInterval);
  }, [showWhatsAppMenu]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const lang = localStorage.getItem("i18nextLng") || "pt";
    i18n.changeLanguage(lang);
    handleLogin(user);
  };

  const handleChangeInput = (name, value) => {
    setUser({ ...user, [name]: value });
    if (name === "password") {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  // Handler para WhatsApp
  const handleWhatsAppClick = (option) => {
    const phone = branding.loginWhatsapp.replace(/[^\d]/g, '');
    let message = "";
    
    switch(option) {
      case "compras":
        message = "Olá! Gostaria de fazer uma compra.";
        break;
      case "suporte":
        message = "Olá! Preciso de suporte técnico.";
        break;
      case "financeiro":
        message = "Olá! Tenho uma dúvida financeira.";
        break;
      case "consultor":
        message = "Olá! Gostaria de falar com um consultor.";
        break;
      default:
        message = "Olá!";
    }
    
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setShowWhatsAppMenu(false);
  };

  // Função para cor da barra de força da senha
  const getPasswordStrengthColor = (strength) => {
    if (strength <= 2) return "#f44336";
    if (strength <= 4) return "#ff9800";
    return "#4caf50";
  };

  // Função para texto da força da senha
  const getPasswordStrengthText = (strength) => {
    if (strength <= 2) return "Senha fraca";
    if (strength > 2 && strength <= 4) return "Senha média";
    return "Senha forte";
  };

  const primaryColor = theme.palette.primary.main || '#2563eb';
  const primaryDark = theme.palette.primary.dark || '#1e40af';

  const globalStyles = `
    @keyframes float {
      0%, 100% { transform: translateY(0px) translateX(0px); }
      25% { transform: translateY(-20px) translateX(10px); }
      50% { transform: translateY(-10px) translateX(-10px); }
      75% { transform: translateY(-30px) translateX(5px); }
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    @keyframes pulse {
      0%, 100% { 
        transform: scale(1);
        opacity: 0.8;
      }
      50% { 
        transform: scale(1.3);
        opacity: 1;
      }
    }
    
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes scaleIn {
      from {
        transform: scale(0);
      }
      to {
        transform: scale(1);
      }
    }

    @keyframes bounceIn {
      0% {
        opacity: 0;
        transform: scale(0.3) translateY(20px);
      }
      50% {
        opacity: 1;
        transform: scale(1.05);
      }
      70% {
        transform: scale(0.9);
      }
      100% {
        transform: scale(1) translateY(0);
      }
    }
  `;

  // Determina se deve usar background customizado ou padrão
  // Considera como "sem background" se for null, vazio, ou o caminho padrão do sistema
  const useCustomBackground = branding.loginBackground 
    && branding.loginBackground.trim() !== '' 
    && !branding.loginBackground.includes('login-background-default.png');

  return (
    <>
      <Helmet>
        <title>Login</title>
        <style>{globalStyles}</style>
      </Helmet>
      
      <div style={{
        background: useCustomBackground 
          ? `url('${resolveImageUrl(branding.loginBackground, "")}')` 
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        backgroundSize: useCustomBackground ? 'cover' : 'auto',
        backgroundPosition: useCustomBackground ? 'center center' : 'initial',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}>
        {/* Animated Lights Background - Apenas se NÃO tiver background customizado */}
        {!useCustomBackground && (
          <>
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 0,
            }}>
              <div style={{
                position: 'absolute',
                borderRadius: '50%',
                filter: 'blur(60px)',
                animation: 'float 6s ease-in-out infinite',
                width: '300px',
                height: '300px',
                background: 'radial-gradient(circle, rgba(99, 102, 241, 0.25) 0%, rgba(99, 102, 241, 0.12) 40%, transparent 70%)',
                top: '15%',
                left: '5%',
                animationDelay: '0s',
              }}></div>
              <div style={{
                position: 'absolute',
                borderRadius: '50%',
                filter: 'blur(60px)',
                animation: 'float 6s ease-in-out infinite',
                width: '250px',
                height: '250px',
                background: 'radial-gradient(circle, rgba(236, 72, 153, 0.22) 0%, rgba(236, 72, 153, 0.1) 50%, transparent 70%)',
                top: '55%',
                right: '10%',
                animationDelay: '2s',
              }}></div>
              <div style={{
                position: 'absolute',
                borderRadius: '50%',
                filter: 'blur(60px)',
                animation: 'float 6s ease-in-out infinite',
                width: '200px',
                height: '200px',
                background: 'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.08) 50%, transparent 70%)',
                bottom: '25%',
                left: '15%',
                animationDelay: '4s',
              }}></div>
              <div style={{
                position: 'absolute',
                borderRadius: '50%',
                filter: 'blur(60px)',
                animation: 'float 6s ease-in-out infinite',
                width: '220px',
                height: '220px',
                background: 'radial-gradient(circle, rgba(168, 85, 247, 0.2) 0%, rgba(168, 85, 247, 0.08) 50%, transparent 70%)',
                top: '40%',
                left: '50%',
                animationDelay: '1s',
              }}></div>
              <div style={{
                position: 'absolute',
                borderRadius: '50%',
                filter: 'blur(60px)',
                animation: 'float 6s ease-in-out infinite',
                width: '280px',
                height: '280px',
                background: 'radial-gradient(circle, rgba(14, 165, 233, 0.22) 0%, rgba(14, 165, 233, 0.1) 50%, transparent 70%)',
                bottom: '10%',
                right: '30%',
                animationDelay: '3s',
              }}></div>
              <div style={{
                position: 'absolute',
                borderRadius: '50%',
                filter: 'blur(60px)',
                animation: 'float 6s ease-in-out infinite',
                width: '240px',
                height: '240px',
                background: 'radial-gradient(circle, rgba(244, 114, 182, 0.2) 0%, rgba(244, 114, 182, 0.09) 50%, transparent 70%)',
                top: '5%',
                right: '25%',
                animationDelay: '5s',
              }}></div>
            </div>

            {/* Geometric Shapes */}
            {!isMobile && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 1,
              }}>
                {/* Quadrados girando */}
                <div style={{
                  position: 'absolute',
                  width: '80px',
                  height: '80px',
                  border: '1px solid rgba(99, 102, 241, 0.5)',
                  background: 'transparent',
                  animation: 'spin 12s linear infinite',
                  filter: 'drop-shadow(0 0 8px rgba(99, 102, 241, 0.5))',
                  top: '15%',
                  left: '8%',
                }}></div>
                <div style={{
                  position: 'absolute',
                  width: '80px',
                  height: '80px',
                  border: '1px solid rgba(236, 72, 153, 0.5)',
                  background: 'transparent',
                  animation: 'spin 15s linear infinite reverse',
                  filter: 'drop-shadow(0 0 8px rgba(236, 72, 153, 0.5))',
                  top: '70%',
                  right: '12%',
                }}></div>
                <div style={{
                  position: 'absolute',
                  width: '80px',
                  height: '80px',
                  border: '1px solid rgba(14, 165, 233, 0.45)',
                  background: 'transparent',
                  animation: 'spin 10s linear infinite',
                  filter: 'drop-shadow(0 0 8px rgba(14, 165, 233, 0.45))',
                  bottom: '25%',
                  left: '25%',
                }}></div>

                {/* Círculos pulsando */}
                <div style={{
                  position: 'absolute',
                  width: '25px',
                  height: '25px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.4), rgba(99, 102, 241, 0.25))',
                  animation: 'pulse 5s ease-in-out infinite',
                  filter: 'drop-shadow(0 0 12px rgba(99, 102, 241, 0.4))',
                  top: '25%',
                  right: '20%',
                  animationDelay: '0s',
                }}></div>
                <div style={{
                  position: 'absolute',
                  width: '35px',
                  height: '35px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.4), rgba(236, 72, 153, 0.25))',
                  animation: 'pulse 6s ease-in-out infinite',
                  filter: 'drop-shadow(0 0 12px rgba(236, 72, 153, 0.4))',
                  bottom: '35%',
                  right: '25%',
                  animationDelay: '1s',
                }}></div>
                <div style={{
                  position: 'absolute',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.35), rgba(14, 165, 233, 0.2))',
                  animation: 'pulse 4.5s ease-in-out infinite',
                  filter: 'drop-shadow(0 0 12px rgba(14, 165, 233, 0.35))',
                  top: '60%',
                  left: '15%',
                  animationDelay: '2s',
                }}></div>
              </div>
            )}
          </>
        )}

        <div style={{
          width: '100%',
          maxWidth: '28rem',
          position: 'relative',
          zIndex: 10,
        }}>
          {/* Login Card */}
          <div 
            style={{
              background: isCardHover ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${isCardHover ? `${primaryColor}66` : `${primaryColor}33`}`,
              transition: 'all 0.3s ease',
              position: 'relative',
              zIndex: 10,
              boxShadow: isCardHover ? `0 20px 50px ${primaryColor}40` : `0 10px 40px ${primaryColor}26`,
              borderRadius: '1rem',
              padding: isMobile ? '2rem' : '2rem',
              transform: isCardHover ? 'translateY(-5px)' : 'translateY(0)',
              animation: 'fadeIn 0.8s ease-out',
            }}
            onMouseEnter={() => setIsCardHover(true)}
            onMouseLeave={() => setIsCardHover(false)}
          >
            {/* Logo */}
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <img
                src={resolveImageUrl(branding.loginLogo, defaultLoginLogo)}
                alt="Logo"
                style={{
                  display: 'block',
                  margin: '0 auto 1.5rem',
                  maxWidth: isMobile ? '180px' : '250px',  // Menor no mobile, maior no desktop
                  height: 'auto',
                }}
              />
              <div style={{
                width: '5rem',
                height: '0.25rem',
                background: `linear-gradient(to right, ${primaryColor}, ${primaryDark})`,
                margin: '0 auto 1rem',
                borderRadius: '9999px',
              }}></div>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '0.5rem',
              }}>
                Bem-vindo
              </h2>
              <p style={{
                color: '#6b7280',
                fontSize: '0.875rem',
              }}>
                Entre na sua conta para continuar
              </p>
            </div>

            {error && <Typography color="error" style={{ marginBottom: '1rem' }}>{error}</Typography>}

            {/* Login Form */}
            <form style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }} onSubmit={handleSubmit}>
              {/* Email Field */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem',
                }}>
                  Email
                </label>
                <div style={{
                  position: 'relative',
                  transform: focusedInput === 'email' ? 'scale(1.02)' : 'scale(1)',
                  transition: 'transform 0.2s',
                }}>
                  <div style={{
                    position: 'absolute',
                    left: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9ca3af',
                    display: 'flex',
                    alignItems: 'center',
                    pointerEvents: 'none',
                  }}>
                    <EmailIcon style={{ fontSize: '1.25rem' }} />
                  </div>
                  <input
                    type="email"
                    value={user.email}
                    onChange={(e) => handleChangeInput('email', e.target.value)}
                    placeholder="seu@email.com"
                    autoComplete="email"
                    required
                    style={{
                      width: '100%',
                      paddingLeft: '3rem',
                      paddingRight: '1rem',
                      paddingTop: '0.75rem',
                      paddingBottom: '0.75rem',
                      background: '#f9fafb',
                      border: focusedInput === 'email' ? `1px solid ${primaryColor}` : '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      color: '#1f2937',
                      transition: 'all 0.2s',
                      outline: 'none',
                      boxShadow: focusedInput === 'email' ? `0 0 0 3px ${primaryColor}33` : 'none',
                      boxSizing: 'border-box',
                    }}
                    onFocus={() => setFocusedInput('email')}
                    onBlur={() => setFocusedInput(null)}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem',
                }}>
                  Senha
                </label>
                <div style={{
                  position: 'relative',
                  transform: focusedInput === 'password' ? 'scale(1.02)' : 'scale(1)',
                  transition: 'transform 0.2s',
                }}>
                  <div style={{
                    position: 'absolute',
                    left: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9ca3af',
                    display: 'flex',
                    alignItems: 'center',
                    pointerEvents: 'none',
                  }}>
                    <LockIcon style={{ fontSize: '1.25rem' }} />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={user.password}
                    onChange={(e) => handleChangeInput('password', e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    style={{
                      width: '100%',
                      paddingLeft: '3rem',
                      paddingRight: '3rem',
                      paddingTop: '0.75rem',
                      paddingBottom: '0.75rem',
                      background: '#f9fafb',
                      border: focusedInput === 'password' ? `1px solid ${primaryColor}` : '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      color: '#1f2937',
                      transition: 'all 0.2s',
                      outline: 'none',
                      boxShadow: focusedInput === 'password' ? `0 0 0 3px ${primaryColor}33` : 'none',
                      boxSizing: 'border-box',
                    }}
                    onFocus={() => setFocusedInput('password')}
                    onBlur={() => setFocusedInput(null)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#6b7280',
                    }}
                  >
                    {showPassword ? <VisibilityOff style={{ fontSize: '1.25rem' }} /> : <Visibility style={{ fontSize: '1.25rem' }} />}
                  </button>
                </div>
                
                {/* Barra de força da senha */}
                {user.password && (
                  <>
                    <div style={{
                      width: '100%',
                      height: '4px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '2px',
                      marginTop: '0.5rem',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        height: '100%',
                        borderRadius: '2px',
                        transition: 'all 0.3s ease',
                        width: `${(passwordStrength / 5) * 100}%`,
                        backgroundColor: getPasswordStrengthColor(passwordStrength)
                      }} />
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      marginTop: '0.25rem',
                      color: getPasswordStrengthColor(passwordStrength)
                    }}>
                      {getPasswordStrengthText(passwordStrength)}
                    </div>
                  </>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '0.875rem',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#374151',
                  cursor: 'pointer',
                }}>
                  <div 
                    onClick={() => handleChangeInput("remember", !user.remember)}
                    style={{
                      position: 'relative',
                      width: '44px',
                      height: '24px',
                      background: user.remember ? `linear-gradient(135deg, ${primaryColor}, ${primaryDark})` : '#e5e7eb',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      left: '2px',
                      width: '20px',
                      height: '20px',
                      background: 'white',
                      borderRadius: '50%',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                      transform: user.remember ? 'translateX(20px)' : 'translateX(0)',
                    }}></div>
                  </div>
                  <span style={{ marginLeft: '0.75rem' }}>Lembrar de mim</span>
                </label>
                <RouterLink
                  to="/forgot-password"
                  style={{
                    color: primaryColor,
                    fontWeight: '500',
                    textDecoration: 'none',
                  }}
                >
                  Esqueceu a senha?
                </RouterLink>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                style={{
                  width: '100%',
                  background: `linear-gradient(to right, ${primaryColor}, ${primaryDark})`,
                  color: 'white',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                  border: 'none',
                  cursor: 'pointer',
                  outline: 'none',
                  transform: isLoginHover ? 'translateY(-1px)' : 'translateY(0)',
                  boxShadow: isLoginHover ? `0 10px 25px ${primaryColor}66` : 'none',
                }}
                onMouseEnter={() => setIsLoginHover(true)}
                onMouseLeave={() => setIsLoginHover(false)}
              >
                Entrar
              </button>

              {/* Sign Up Button */}
              {userCreationEnabled && (
                <button
                  type="button"
                  onClick={() => window.location.href = '/signup'}
                  style={{
                    width: '100%',
                    background: 'transparent',
                    color: primaryColor,
                    padding: '0.75rem 1rem',
                    borderRadius: '0.5rem',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    border: `2px solid ${primaryColor}`,
                    cursor: 'pointer',
                    outline: 'none',
                    transform: isSignupHover ? 'translateY(-1px)' : 'translateY(0)',
                    boxShadow: isSignupHover ? `0 10px 25px ${primaryColor}33` : 'none',
                  }}
                  onMouseEnter={() => setIsSignupHover(true)}
                  onMouseLeave={() => setIsSignupHover(false)}
                >
                  Cadastre-se
                </button>
              )}
            </form>
          </div>
        </div>

        {/* WhatsApp Widget */}
        <div style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          zIndex: 1000,
        }}>
          {/* Balão "Estamos online" */}
          {showBalloon && !showWhatsAppMenu && (
            <div style={{
              position: 'absolute',
              bottom: '80px',
              right: '0',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              borderRadius: '1rem',
              padding: '1rem 1.25rem',
              boxShadow: '0 10px 40px rgba(37, 99, 235, 0.25)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              minWidth: '200px',
              animation: 'bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
              cursor: 'pointer',
            }}
            onClick={() => {
              setShowBalloon(false);
              setShowWhatsAppMenu(true);
            }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}>
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: '#25D366',
                  boxShadow: '0 0 10px rgba(37, 211, 102, 0.6)',
                  animation: 'pulse 2s ease-in-out infinite',
                }}></div>
                <div>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '0.125rem',
                  }}>
                    Olá! Estamos online.
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                  }}>
                    Como podemos ajudar?
                  </div>
                </div>
              </div>
              <div style={{
                position: 'absolute',
                bottom: '-8px',
                right: '20px',
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: '8px solid rgba(255, 255, 255, 0.98)',
                filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
              }}></div>
            </div>
          )}

          {/* Menu de opções */}
          {showWhatsAppMenu && (
            <div style={{
              position: 'absolute',
              bottom: '80px',
              right: '0',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              borderRadius: '1rem',
              boxShadow: '0 10px 40px rgba(37, 99, 235, 0.2)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              width: '320px',
              maxHeight: '480px',
              overflow: 'hidden',
              animation: 'slideUp 0.3s ease-out',
            }}>
              {/* Cabeçalho */}
              <div style={{
                background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                padding: '1rem 1.25rem',
                color: 'white',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '0.5rem',
                }}>
                  <div style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                  }}>
                    <WhatsAppIcon style={{ fontSize: 24, color: '#fff' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '1.0625rem',
                      fontWeight: '700',
                      marginBottom: '0.25rem',
                    }}>
                      Atendimento
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.8125rem',
                      opacity: 0.95,
                    }}>
                      <div style={{
                        width: '7px',
                        height: '7px',
                        borderRadius: '50%',
                        background: '#fff',
                        boxShadow: '0 0 8px rgba(255, 255, 255, 0.8)',
                        animation: 'pulse 2s ease-in-out infinite',
                      }}></div>
                      <span>Estamos online</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowWhatsAppMenu(false)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      color: 'white',
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div style={{
                  fontSize: '0.8125rem',
                  opacity: 0.9,
                  lineHeight: 1.4,
                }}>
                  Olá! 👋 Como podemos ajudar você hoje?
                </div>
              </div>

              {/* Opções */}
              <div style={{ padding: '0.875rem 1rem 1rem' }}>
                <div style={{
                  fontSize: '0.6875rem',
                  fontWeight: '500',
                  color: '#94a3b8',
                  marginBottom: '0.75rem',
                  paddingLeft: '0.25rem',
                  letterSpacing: '0.08em',
                }}>
                  Selecione um departamento
                </div>
                {[
                  { id: 'compras', label: 'Compras', color: 'rgba(59, 130, 246, 0.1)', iconColor: '#3b82f6' },
                  { id: 'suporte', label: 'Suporte Técnico', color: 'rgba(168, 85, 247, 0.1)', iconColor: '#a855f7' },
                  { id: 'financeiro', label: 'Financeiro', color: 'rgba(34, 197, 94, 0.1)', iconColor: '#22c55e' },
                  { id: 'consultor', label: 'Falar com Consultor', color: 'rgba(236, 72, 153, 0.1)', iconColor: '#ec4899' },
                ].map((option, index) => (
                  <button
                    key={option.id}
                    onClick={() => handleWhatsAppClick(option.id)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.875rem',
                      padding: '0.75rem 0.875rem',
                      background: 'transparent',
                      border: '1px solid rgba(226, 232, 240, 0.8)',
                      borderRadius: '0.625rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontSize: '0.875rem',
                      color: '#1f2937',
                      fontWeight: '500',
                      textAlign: 'left',
                      marginBottom: index < 3 ? '0.5rem' : '0',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = option.color;
                      e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                      e.currentTarget.style.transform = 'translateX(4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '0.5rem',
                      background: option.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: option.iconColor,
                      fontSize: '1.25rem',
                    }}>
                      {index === 0 ? '🛒' : index === 1 ? '⚙️' : index === 2 ? '💰' : '👤'}
                    </div>
                    <span style={{ flex: 1 }}>{option.label}</span>
                    <span style={{ opacity: 0.4 }}>→</span>
                  </button>
                ))}
              </div>

              {/* Footer */}
              <div style={{
                padding: '0.625rem 1rem',
                borderTop: '1px solid rgba(226, 232, 240, 0.8)',
                background: 'rgba(249, 250, 251, 0.5)',
                fontSize: '0.6875rem',
                color: '#6b7280',
                textAlign: 'center',
              }}>
                Tempo de resposta: <span style={{ fontWeight: '600', color: '#25D366' }}>Imediato</span>
              </div>
            </div>
          )}

          {/* Botão principal */}
          <button
            onClick={() => setShowWhatsAppMenu(!showWhatsAppMenu)}
            onMouseEnter={() => setIsWhatsAppHover(true)}
            onMouseLeave={() => setIsWhatsAppHover(false)}
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: isWhatsAppHover 
                ? 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' 
                : 'linear-gradient(135deg, #25D366 0%, #25D366 100%)',
              border: 'none',
              cursor: 'pointer',
              boxShadow: isWhatsAppHover 
                ? '0 12px 35px rgba(37, 211, 102, 0.5)' 
                : '0 8px 25px rgba(37, 211, 102, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: isWhatsAppHover ? 'scale(1.1) rotate(10deg)' : 'scale(1) rotate(0deg)',
              animation: 'scaleIn 0.5s ease-out',
              position: 'relative',
            }}
          >
            <div style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: 'rgba(37, 211, 102, 0.4)',
              animation: 'pulse 2s ease-in-out infinite',
            }}></div>
            
            <WhatsAppIcon style={{ fontSize: 36, color: 'white', position: 'relative', zIndex: 1 }} />

            {showWhatsAppMenu && (
              <div style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                border: '2px solid white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.625rem',
                fontWeight: 'bold',
                color: 'white',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}>
                4
              </div>
            )}
          </button>

          {/* Tooltip */}
          {!showWhatsAppMenu && isWhatsAppHover && (
            <div style={{
              position: 'absolute',
              bottom: '50%',
              right: '80px',
              transform: 'translateY(50%)',
              background: 'rgba(31, 41, 55, 0.95)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              animation: 'fadeIn 0.2s ease-out',
            }}>
              Fale conosco no WhatsApp
              <div style={{
                position: 'absolute',
                right: '-6px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderLeft: '6px solid rgba(31, 41, 55, 0.95)',
              }}></div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Login;